'use client';

import { useMemo, useState } from 'react';
import {
  computeInvoiceTotals,
  formatMoney,
  type InvoiceLineItem,
} from '@/lib/browser-widget-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const PRINT_CSS = `@media print {
  body * { visibility: hidden !important; }
  #cleanor-invoice-print, #cleanor-invoice-print * { visibility: visible !important; }
  #cleanor-invoice-print {
    position: absolute !important;
    left: 0; top: 0;
    width: 100%;
    padding: 24px !important;
    background: #ffffff !important;
    color: #111827 !important;
    box-shadow: none !important;
  }
}`;

const CURRENCIES = [
  { value: '$', label: 'USD ($)' },
  { value: '€', label: 'EUR (€)' },
  { value: '£', label: 'GBP (£)' },
  { value: '¥', label: 'JPY (¥)' },
  { value: '₹', label: 'INR (₹)' },
  { value: '', label: 'No symbol' },
];

const initialItems: InvoiceLineItem[] = [
  { description: 'Design work', quantity: '10', price: '75' },
  { description: 'Hosting (monthly)', quantity: '1', price: '20' },
];

export default function BrowserInvoiceGenerator() {
  const [seller, setSeller] = useState('Your Company LLC\n123 Market St\nemail@company.com');
  const [client, setClient] = useState('Client Name\n456 Client Ave\nclient@example.com');
  const [invoiceNumber, setInvoiceNumber] = useState('INV-0001');
  const [invoiceDate, setInvoiceDate] = useState('2026-07-04');
  const [dueDate, setDueDate] = useState('2026-07-18');
  const [currency, setCurrency] = useState('$');
  const [taxPercent, setTaxPercent] = useState('0');
  const [notes, setNotes] = useState('Payment due within 14 days. Thank you for your business.');
  const [items, setItems] = useState<InvoiceLineItem[]>(initialItems);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const totals = useMemo(() => computeInvoiceTotals(items, taxPercent), [items, taxPercent]);

  function updateItem(index: number, key: keyof InvoiceLineItem, value: string) {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  }

  function addItem() {
    setItems((prev) => [...prev, { description: '', quantity: '1', price: '0' }]);
  }

  function removeItem(index: number) {
    setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  }

  function handlePrint() {
    if (typeof window !== 'undefined') {
      window.print();
    }
  }

  async function handleCopyTotal() {
    try {
      await copyText(formatMoney(totals.total, currency));
      setCopyMessage('Total copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  const cellStyle: React.CSSProperties = {
    padding: '8px 10px',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '14px',
  };

  return (
    <div className="tool-live-shell">
      <style>{PRINT_CSS}</style>
      <div className="tool-live-header">
        <span className="tool-live-badge">Invoice</span>
        <h2 className="tool-live-title">Invoice generator</h2>
        <p className="tool-live-subtitle">
          Fill in the details, preview a clean invoice, then print or save it as a PDF. Everything
          stays in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>From (seller)</span>
              <textarea onChange={(e) => setSeller(e.target.value)} rows={3} value={seller} />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Bill to (client)</span>
              <textarea onChange={(e) => setClient(e.target.value)} rows={3} value={client} />
            </label>
            <label className="tool-live-field">
              <span>Invoice #</span>
              <input
                onChange={(e) => setInvoiceNumber(e.target.value)}
                type="text"
                value={invoiceNumber}
              />
            </label>
            <label className="tool-live-field">
              <span>Currency</span>
              <select onChange={(e) => setCurrency(e.target.value)} value={currency}>
                {CURRENCIES.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Invoice date</span>
              <input
                onChange={(e) => setInvoiceDate(e.target.value)}
                type="text"
                value={invoiceDate}
              />
            </label>
            <label className="tool-live-field">
              <span>Due date</span>
              <input onChange={(e) => setDueDate(e.target.value)} type="text" value={dueDate} />
            </label>
            <label className="tool-live-field">
              <span>Tax %</span>
              <input
                inputMode="decimal"
                onChange={(e) => setTaxPercent(e.target.value)}
                type="text"
                value={taxPercent}
              />
            </label>
          </div>

          <div style={{ marginTop: '16px' }}>
            <span
              style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}
            >
              Line items
            </span>
            {items.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 70px 90px 32px',
                  gap: '8px',
                  marginBottom: '8px',
                  alignItems: 'center',
                }}
              >
                <input
                  aria-label="Description"
                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                  placeholder="Description"
                  type="text"
                  value={item.description}
                />
                <input
                  aria-label="Quantity"
                  inputMode="decimal"
                  onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                  placeholder="Qty"
                  type="text"
                  value={item.quantity}
                />
                <input
                  aria-label="Unit price"
                  inputMode="decimal"
                  onChange={(e) => updateItem(index, 'price', e.target.value)}
                  placeholder="Price"
                  type="text"
                  value={item.price}
                />
                <button
                  aria-label="Remove line item"
                  className="button button-secondary"
                  onClick={() => removeItem(index)}
                  style={{ padding: '6px 0', minWidth: 0 }}
                  type="button"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              className="button button-secondary"
              onClick={addItem}
              style={{ marginTop: '4px' }}
              type="button"
            >
              + Add line item
            </button>
          </div>

          <label className="tool-live-field tool-live-field-full" style={{ marginTop: '16px' }}>
            <span>Notes</span>
            <textarea onChange={(e) => setNotes(e.target.value)} rows={2} value={notes} />
          </label>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handlePrint} type="button">
              Download / Print PDF
            </button>
            <button className="button button-secondary" onClick={handleCopyTotal} type="button">
              Copy total
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            id="cleanor-invoice-print"
            style={{
              background: '#ffffff',
              color: '#111827',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid #e5e7eb',
              fontFamily: 'Arial, Helvetica, sans-serif',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '16px',
                marginBottom: '20px',
              }}
            >
              <div>
                <div style={{ fontSize: '22px', fontWeight: 700 }}>Invoice</div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                  {invoiceNumber}
                </div>
              </div>
              <div style={{ fontSize: '13px', color: '#4b5563', textAlign: 'right' }}>
                <div>Date: {invoiceDate}</div>
                <div>Due: {dueDate}</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '24px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px' }}>
                <div
                  style={{
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    color: '#9ca3af',
                    marginBottom: '4px',
                  }}
                >
                  From
                </div>
                <div style={{ fontSize: '13px', whiteSpace: 'pre-line' }}>{seller}</div>
              </div>
              <div style={{ flex: '1 1 180px' }}>
                <div
                  style={{
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    color: '#9ca3af',
                    marginBottom: '4px',
                  }}
                >
                  Bill to
                </div>
                <div style={{ fontSize: '13px', whiteSpace: 'pre-line' }}>{client}</div>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
              <thead>
                <tr>
                  <th
                    style={{ ...cellStyle, textAlign: 'left', color: '#6b7280', fontWeight: 600 }}
                  >
                    Description
                  </th>
                  <th
                    style={{ ...cellStyle, textAlign: 'right', color: '#6b7280', fontWeight: 600 }}
                  >
                    Qty
                  </th>
                  <th
                    style={{ ...cellStyle, textAlign: 'right', color: '#6b7280', fontWeight: 600 }}
                  >
                    Price
                  </th>
                  <th
                    style={{ ...cellStyle, textAlign: 'right', color: '#6b7280', fontWeight: 600 }}
                  >
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {totals.lines.map((line, index) => (
                  <tr key={index}>
                    <td style={{ ...cellStyle, textAlign: 'left' }}>
                      {line.description || <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{line.quantity}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      {formatMoney(line.price, currency)}
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      {formatMoney(line.amount, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <table style={{ minWidth: '220px' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '4px 10px', fontSize: '13px', color: '#4b5563' }}>
                      Subtotal
                    </td>
                    <td
                      style={{
                        padding: '4px 10px',
                        fontSize: '13px',
                        textAlign: 'right',
                      }}
                    >
                      {formatMoney(totals.subtotal, currency)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 10px', fontSize: '13px', color: '#4b5563' }}>
                      Tax ({totals.taxRate}%)
                    </td>
                    <td style={{ padding: '4px 10px', fontSize: '13px', textAlign: 'right' }}>
                      {formatMoney(totals.taxAmount, currency)}
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        padding: '8px 10px',
                        fontSize: '15px',
                        fontWeight: 700,
                        borderTop: '2px solid #111827',
                      }}
                    >
                      Total
                    </td>
                    <td
                      style={{
                        padding: '8px 10px',
                        fontSize: '15px',
                        fontWeight: 700,
                        textAlign: 'right',
                        borderTop: '2px solid #111827',
                      }}
                    >
                      {formatMoney(totals.total, currency)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {notes ? (
              <div
                style={{
                  marginTop: '20px',
                  paddingTop: '12px',
                  borderTop: '1px solid #e5e7eb',
                  fontSize: '12px',
                  color: '#6b7280',
                  whiteSpace: 'pre-line',
                }}
              >
                {notes}
              </div>
            ) : null}
          </div>
          <p className="tool-live-panel-note">
            Use your browser&apos;s print dialog and choose &ldquo;Save as PDF&rdquo;. Nothing is
            uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
